/**
 * Hero 3D scene for the coming-soon landing page.
 *
 * We don't have a textured 3D model of the IntraAlpha bird, so this scene
 * is a stylized abstraction that complements the wordmark instead of
 * competing with it: a flock of small instanced "bird" shapes orbiting
 * a central glowing core, plus a subtle particle field for depth.
 *
 * The full bird PNG is rendered in the foreground composite (in Landing.tsx)
 * — this WebGL scene is the ambient backdrop.
 */

import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Stars } from "@react-three/drei";
import * as THREE from "three";

const BRAND_400 = new THREE.Color("#71c68d");
const BRAND_500 = new THREE.Color("#508154");
const INK_400 = new THREE.Color("#8c8ca1");

/** Glowing core in the center of the scene. */
function GlowCore() {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.getElapsedTime();
    meshRef.current.scale.setScalar(1 + Math.sin(t * 1.4) * 0.08);
  });

  return (
    <Float speed={1.6} rotationIntensity={0.4} floatIntensity={0.8}>
      <mesh ref={meshRef}>
        <icosahedronGeometry args={[0.7, 1]} />
        <meshBasicMaterial color={BRAND_400} wireframe transparent opacity={0.6} />
      </mesh>
    </Float>
  );
}

/** A swarm of "bird" tetrahedra orbiting the core in two rings. */
function BirdSwarm({ count = 60, radius = 4 }: { count?: number; radius?: number }) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const tmpObj = useMemo(() => new THREE.Object3D(), []);

  // Stable per-bird parameters (don't randomize every frame)
  const birds = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const ring = i % 2 === 0 ? 1 : 1.4;
        const phase = (i / count) * Math.PI * 2;
        const tilt = (Math.random() - 0.5) * 0.6;
        const speed = 0.25 + Math.random() * 0.35;
        return { ring, phase, tilt, speed };
      }),
    [count],
  );

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    birds.forEach((b, i) => {
      const r = radius * b.ring;
      const angle = b.phase + t * b.speed;
      tmpObj.position.set(
        Math.cos(angle) * r,
        Math.sin(angle * 0.7 + b.phase) * 1.6 + b.tilt,
        Math.sin(angle) * r,
      );
      tmpObj.rotation.set(
        Math.sin(angle * 2) * 0.4,
        angle + Math.PI / 2,
        Math.cos(angle * 2) * 0.4,
      );
      tmpObj.scale.setScalar(0.18 + Math.sin(angle * 3) * 0.04);
      tmpObj.updateMatrix();
      ref.current!.setMatrixAt(i, tmpObj.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, count]}>
      <coneGeometry args={[0.4, 0.9, 4]} />
      <meshStandardMaterial color={BRAND_500} emissive={BRAND_500} emissiveIntensity={0.4} />
    </instancedMesh>
  );
}

/** Drifting fog plane that gives the scene atmospheric depth. */
function FogPlane() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.z = state.clock.getElapsedTime() * 0.04;
  });
  return (
    <mesh ref={ref} position={[0, 0, -8]}>
      <planeGeometry args={[40, 40]} />
      <meshBasicMaterial color={INK_400} transparent opacity={0.04} depthWrite={false} />
    </mesh>
  );
}

interface BirdSceneProps {
  className?: string;
  /** Lower = more ambient, higher = more activity. */
  intensity?: number;
}

export function BirdScene({ className, intensity = 1 }: BirdSceneProps) {
  return (
    <Canvas
      className={className}
      camera={{ position: [0, 1, 8], fov: 55 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
    >
      <ambientLight intensity={0.4} />
      <pointLight position={[5, 5, 5]} intensity={1.2 * intensity} color={BRAND_400} />
      <pointLight position={[-5, -3, 4]} intensity={0.6 * intensity} color="#3d7340" />

      <GlowCore />
      <BirdSwarm count={Math.round(60 * intensity)} radius={4} />
      <FogPlane />

      <Stars
        radius={70}
        depth={50}
        count={2000}
        factor={3}
        saturation={0}
        fade
        speed={0.4}
      />
    </Canvas>
  );
}
